import { useCallback, useMemo } from "react";
import { useLocation, useMatches, useNavigate } from "react-router-dom";

/**
 * The way back from a detail page to the listing it was opened from.
 *
 * Derived, not declared: no page registers anything, so any future detail
 * route gets its back link for free. Two facts from the router make that
 * possible — the app uses a data router, so `useMatches` exposes both.
 *
 * 1. **Is this a detail page at all?** Only if the last path segment is one of
 *    the matched route's params. A listing whose path ends in a static segment
 *    has no back target, which is exactly when the button should stay hidden.
 *
 * 2. **Which listing?** The parent route's path plus the first segment the
 *    detail route adds to it. Dropping only the id is not enough: detail routes
 *    carry a static kind segment (`assessments/pnoe/:measurementId`), and
 *    `…/assessments/pnoe` is not a route — the listing is `…/assessments`.
 *
 *      …/assessments/pnoe/:measurementId  → …/assessments
 *      …/programming/:serviceId           → …/programming
 *      /client/orders/pnoe-test/:id       → /client/orders
 *      …/prescription/:prescriptionId     → …/prescription
 *
 * The query string rides along: in a client workspace `?id=<client>` is what
 * scopes the listing, so dropping it would land on an empty page.
 */
export interface BackNavigation {
  /** Listing path, without the query string. */
  target: string;
  /** Navigate to the listing, preserving the current query string. */
  goBack: () => void;
}

/** Splat matches (`path: "*"`) capture a whole tail, not an id. */
const SPLAT_PARAM = "*";

const segmentsOf = (pathname: string) => pathname.split("/").filter(Boolean);

/**
 * The listing path for a detail page, or null when this is not one.
 *
 * `parentPathname` is the pathname of the route one level up — the listing's
 * own route or the shell it lives in.
 */
export const getBackNavigationTarget = (
  pathname: string,
  parentPathname: string,
  params: Readonly<Record<string, string | undefined>>
): string | null => {
  const segments = segmentsOf(pathname);
  const last = segments[segments.length - 1];
  if (!last) return null;

  // (1) The trailing segment has to be an id this route matched.
  const idValues = Object.entries(params)
    .filter(([key, value]) => key !== SPLAT_PARAM && Boolean(value))
    .map(([, value]) => value);
  if (!idValues.includes(last)) return null;

  // (2) Parent, plus the one segment the detail route adds on top of it. When
  // the detail route adds nothing but the id (`prescription/:prescriptionId`
  // nested under `prescription`), the parent *is* the listing.
  const parentSegments = segmentsOf(parentPathname);
  if (parentSegments.length >= segments.length) return null;
  const firstOwnSegment = segments[parentSegments.length];
  const listingSegments =
    firstOwnSegment === last
      ? parentSegments
      : [...parentSegments, firstOwnSegment];

  if (!listingSegments.length) return null;
  return `/${listingSegments.join("/")}`;
};

/**
 * `null` on a listing page, `{ target, goBack }` on a detail page. Consumed by
 * `Breadcrumb`, which renders the back button in the top bar.
 */
const useBackNavigation = (): BackNavigation | null => {
  const matches = useMatches();
  const location = useLocation();
  const navigate = useNavigate();

  const target = useMemo(() => {
    // The deepest match is the rendered leaf; the one above it is the route
    // the listing hangs off. A pathless layout route in between simply repeats
    // its parent's pathname, which is the right answer anyway.
    const deepest = matches[matches.length - 1];
    const parent = matches[matches.length - 2];
    if (!deepest || !parent) return null;

    return getBackNavigationTarget(
      location.pathname,
      parent.pathname,
      (deepest.params ?? {}) as Record<string, string | undefined>
    );
  }, [matches, location.pathname]);

  const goBack = useCallback(() => {
    if (target) navigate(`${target}${location.search}`);
  }, [navigate, target, location.search]);

  return target ? { target, goBack } : null;
};

export default useBackNavigation;
